import {
  AlertNotification,
  Button,
  HandleIcon,
  IconButton,
  Input,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Typography,
  XIcon,
} from "@commonsku/styles";
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { getFullItem } from "../../../selectors";
import { deleteVariant, validateExternalItem } from "../../../actions/item";
import ExternalSkuInput from "./ExternalSkuInput";
import { ItemVariant } from "../../../interfaces/ItemVariant";
import SizeAndColorDropdown from "./SizeAndColorDropdown";

const duplicateChecker = (
  variants: ItemVariant[],
  currentInputs: Record<string, string> = {}
) => {
  const externalIds: Record<string, string[]> = variants.reduce((v, m) => {
    const externalId = currentInputs[m.item_variant_id] || m.external_id;
    if (!externalId) return v;
    if (!v[externalId]) {
      v[externalId] = [];
    }
    v[externalId].push(m.item_variant_id);
    return v;
  }, {} as Record<string, string[]>);

  const duplicates = Object.entries(externalIds)
    .filter(([_, ids]) => ids.length > 1)
    .flatMap(([_, ids]) => ids);

  return duplicates;
};
interface VariantsProps {
  item_id: string;
}

const Variants = ({ item_id }: VariantsProps) => {
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [hoveredElement, setHoveredElement] = useState<string>("");
  const [duplicateSku, setDuplicateSku] = useState<string[]>([]);
  const [uniqueSkuError, setUniqueSkuError] = useState<
    Record<string, string | null>
  >({});
  const [, setCurrentInputs] = useState<Record<string, string>>({});
  const [externalItemValidationError, setExternalItemValidationError] =
    useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const item = useSelector((state) => getFullItem(state, { item_id }));
  const dispatch = useDispatch();

  useEffect(() => {
    if (showDropdown && dropdownRef.current) {
      dropdownRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [showDropdown]);

  const handleDeleteVariant = (item_id: string, item_variant_id: string) => {
    dispatch(deleteVariant(item_id, item_variant_id));
  };

  const handleExternalItemValidation = (item_id: string) => {
    (dispatch as any)(validateExternalItem(item_id))
      .then((res: any) => {
        const validationFailed = Object.values(
          res.payload.item_validation
        ).some((variant: any) => variant?.validated === false);
        setExternalItemValidationError(validationFailed);
      })
      .catch((error: any) => {
        console.error(error);
        setExternalItemValidationError(true);
      });
  };

  const handleSetDuplicateExternalSku = useCallback(
    (variantId: string, externalId: string) => {
      setCurrentInputs((prev) => {
        const newInputs = { ...prev, [variantId]: externalId };
        const duplicates = duplicateChecker(item?.variants || [], newInputs);
        setDuplicateSku(duplicates);
        return newInputs;
      });
    },
    [item?.variants]
  );

  const handleRemoveUpdatedDuplicates = useCallback((variantId: string) => {
    setDuplicateSku((prev) => prev.filter((id) => id !== variantId));
    setUniqueSkuError((prev) => {
      const newErrors = { ...prev };
      delete newErrors[variantId];
      return newErrors;
    });
  }, []);

  const handleUniqueSkuError = useCallback(
    (variantId: string, error: string | null) => {
      setUniqueSkuError((prev) => {
        if (error === null) {
          const newErrors = { ...prev };
          delete newErrors[variantId];
          return newErrors;
        }
        return { ...prev, [variantId]: error };
      });
    },
    []
  );

  const sortedVariants = useMemo(() => {
    return (
      Array.isArray(item?.variants!) &&
      item.variants.sort(
        (a, b) =>
          new Date(a.date_created).getTime() -
          new Date(b.date_created).getTime()
      )
    );
  }, [item?.variants]);

  return (
    <div className="row" style={{ position: "relative" }}>
      <div className="large-9 columns flex-hdr-row" style={{ height: 56 }}>
        <h2>Variants</h2>
        {(duplicateSku.length > 0 ||
          Object.keys(uniqueSkuError).length > 0) && (
          <AlertNotification
            alertType="error"
            style={{ marginLeft: 24, height: 56, alignItems: "center" }}
          >
            {externalItemValidationError
              ? "Products variant not found in the shopify store"
              : "Please resolve duplicate SKUs"}
          </AlertNotification>
        )}
      </div>
      <div className="row" style={{ margin: 0, padding: 16 }}>
        <Table>
          <THead>
            <TR
              style={{
                backgroundColor: "#F8F8F8",
                display: "flex",
                width: "100%",
              }}
            >
              <TH style={{ width: 32, background: "none" }}></TH>
              <TH
                style={{
                  width: "33%",
                  background: "none",
                  fontWeight: 500,
                  fontSize: 16,
                }}
              >
                Color/Size
              </TH>
              <TH
                style={{
                  width: "33%",
                  background: "none",
                  display: "flex",
                  flexDirection: "column",
                  fontWeight: 500,
                  fontSize: 16,
                }}
              >
                External SKU
                <Typography.Text
                  as="span"
                  style={{ fontWeight: 400, fontSize: 14 }}
                >
                  Successfully mapped variants will be locked and cannot be
                  deleted once the sales order is created.
                </Typography.Text>
              </TH>
            </TR>
          </THead>
          {sortedVariants ? (
            <TBody>
              {sortedVariants.map((variant: ItemVariant) => {
                let displayValue = "Undefined";
                if (variant?.product_sku) {
                  if (variant?.product_sku.sku_options) {
                    try {
                      const skuOptions = JSON.parse(
                        variant?.product_sku.sku_options
                      );
                      const color = skuOptions.color;
                      const size = skuOptions.size;
                      if (color && size) {
                        displayValue = `${color}/${size}`;
                      } else if (color) {
                        displayValue = color;
                      } else if (size) {
                        displayValue = size;
                      } else {
                        displayValue = variant?.product_sku.sku || "TBD";
                      }
                    } catch (e) {
                      console.error(
                        "Failed to parse sku_options:",
                        variant?.product_sku.sku_options,
                        e
                      );
                      displayValue = variant?.product_sku.sku || "TBD";
                    }
                  } else {
                    displayValue = variant?.product_sku.sku || "TBD";
                  }
                } else {
                  const colorName = variant?.color?.color_name;
                  const sizeName = variant?.size?.size_name;

                  if (colorName && sizeName) {
                    displayValue = `${colorName}/${sizeName}`;
                  } else if (colorName) {
                    displayValue = colorName;
                  } else if (sizeName) {
                    displayValue = sizeName;
                  }
                }

                return (
                  <TR
                    key={variant?.item_variant_id}
                    style={{
                      display: "flex",
                      width: "100%",
                    }}
                    onMouseEnter={() =>
                      setHoveredElement(variant?.item_variant_id)
                    }
                    onMouseLeave={() => setHoveredElement("")}
                  >
                    <TD
                      style={{
                        width: 32,
                        alignContent: "center",
                      }}
                    >
                      {hoveredElement === variant?.item_variant_id &&
                        variant?.locked !== 1 && (
                          <XIcon
                            onClick={() =>
                              handleDeleteVariant(
                                variant?.item_id,
                                variant?.item_variant_id
                              )
                            }
                            width={24}
                            height={24}
                            pointer
                            disabled={variant?.locked === 1}
                          />
                        )}
                    </TD>
                    <TD style={{ width: "33%" }}>
                      <Input
                        value={displayValue}
                        style={{ marginBottom: 0, borderRadius: "5px" }}
                        contentEditable={false}
                        readOnly
                      />
                    </TD>
                    <TD style={{ width: "33%" }}>
                      <ExternalSkuInput
                        key={variant?.item_variant_id}
                        variant={variant}
                        isDuplicate={duplicateSku.includes(
                          variant?.item_variant_id
                        )}
                        uniqueSkuError={
                          uniqueSkuError[variant?.item_variant_id] || null
                        }
                        onDuplicateChange={handleSetDuplicateExternalSku}
                        onErrorChange={handleUniqueSkuError}
                        onSuccess={handleRemoveUpdatedDuplicates}
                      />
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          ) : null}
        </Table>
        <div style={{ width: "100%" }}>
          <IconButton
            variant="primary"
            size="medium"
            Icon={<HandleIcon />}
            iconPosition="left"
            onClick={(e) => {
              e.stopPropagation();
              setShowDropdown(!showDropdown);
            }}
            style={{ marginBottom: 0 }}
          >
            Add sizes and colors
          </IconButton>
          <Button
            variant="secondary"
            size="medium"
            style={{ float: "right", marginBottom: 0 }}
            onClick={() => handleExternalItemValidation(item_id)}
            hidden
          >
            Validate Items
          </Button>
        </div>
      </div>
      {showDropdown && (
        <div ref={dropdownRef}>
          <SizeAndColorDropdown
            item_id={item_id}
            onClose={() => setShowDropdown(false)}
          />
        </div>
      )}
    </div>
  );
};

export default Variants;
